import { useEffect } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { Dialog, Group, Button, Text, TextProps } from '@mantine/core';
import { useSelector, useDispatch } from 'react-redux';
import { selectAllErrors } from '../../../redux/selectors/errorSelector';
import { clearError } from '../../../redux/reducers/errorSlice';
import { selectSuccess } from '../../../redux/selectors/successSelector';
import { clearSuccess } from '../../../redux/reducers/successSlice';

interface DialogStatusProps {
  dialogProps?: any;
  textProps?: Partial<TextProps>;
  errorColor?: string;
  successColor?: string;
  showButton?: boolean;
}

function DialogStatus({
  dialogProps = {},
  textProps = {},
  errorColor = 'red',
  successColor = 'green',
  showButton = true
}: DialogStatusProps) {
  const dispatch = useDispatch();
  const [opened, { toggle, close }] = useDisclosure(false);
  const errors = useSelector(selectAllErrors);
  const successMessage = useSelector(selectSuccess);

  // Get the most recent error if any exists
  const mostRecentError = Object.values(errors)[Object.values(errors).length - 1];
  const errorMessage = mostRecentError?.error?.errorMessage;

  useEffect(() => {
    if (errorMessage || successMessage) {
      toggle();
    }
  }, [errorMessage, successMessage]);

  useEffect(() => {
    if (opened) {
      const timer = setTimeout(() => {
        close();
        dispatch(clearError());
        dispatch(clearSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [opened, dispatch, close]);

  const handleClose = () => {
    close();
    dispatch(clearError());
    dispatch(clearSuccess());
  };

  return (
    <>
      <Dialog
        opened={opened}
        withCloseButton
        onClose={handleClose}
        size="lg"
        radius="md"
        position={dialogProps.position}
        styles={dialogProps.styles}
        zIndex={1000}
      >
        <Text
          size="md"
          mb={showButton ? "xs" : "0"}
          fw={errorMessage ? 700 : 500}
          c={errorMessage ? errorColor : successColor}
          {...textProps}
        >
          {errorMessage || successMessage}
        </Text>

        {showButton && <Group align="center">
          <Button onClick={handleClose}>
            Close
          </Button>
        </Group>}
      </Dialog>
    </>
  );
}

export default DialogStatus;